// Plays the animation of the mood that the server writes in `data-mood`. The 13 sprites weigh about 4 MB. They load in
// the background, so they never delay the panel.
//
// Each sprite is one image with all its frames in a row (a strip). A `steps()` animation moves through the frames, so no
// JavaScript runs for each frame. A strip loops, unless it has an `iterations` count.
const STRIPS = {
  idle: { frames: 24, ms: 4000 },
  "idle-sway": { frames: 24, ms: 2400, iterations: 1 },
  "idle-right-in": { frames: 8, ms: 500, iterations: 1 },
  "idle-right": { frames: 24, ms: 4000 },
  "idle-right-out": { frames: 8, ms: 500, iterations: 1 },
  "idle-right-smile": { frames: 24, ms: 4000 },
  "idle-smile": { frames: 24, ms: 4000 },
  thinking: { frames: 24, ms: 2600 },
  surprise: { frames: 26, ms: 2600 },
  success: { frames: 30, ms: 2700, direction: "alternate" },
  error: { frames: 24, ms: 2400, direction: "alternate" },
  sleep: { frames: 24, ms: 4000 },
  "sleep-nod": { frames: 24, ms: 2400, iterations: 1 },
};
const REDUCED_MOTION = window.matchMedia("(prefers-reduced-motion: reduce)");
const SPRITE_BASE = "/images/petronille_stage/";
const REVEAL_TIMEOUT_MS = 400;

const ONCE_MS = { success: 4000, error: 4000, surprise: 2800 };
const SLEEP_AFTER_MS = 45000;
const SLEEP_RETRY_MS = 3000;
const ACTIVITY_EVENTS = ["mousemove", "keydown", "click"];
const VARIATION_MS = { "idle-sway": 2400, "idle-smile": 4000, "sleep-nod": 2400 };
const TURN_MS = 500;
const RIGHT_LOOK_MS = 5000;
const IDLE_VARIATIONS = ["idle-sway", "idle-right", "idle-smile"];
const SLEEP_VARIATIONS = ["sleep-nod", "dream"];

// Resolves when the sprite is decoded, or after a short timeout. So a slow network never blocks the figure.
function decode(name) {
  const image = new Image();
  image.src = `${SPRITE_BASE}${name}.webp`;
  const ready = image.decode ? image.decode().catch(() => {}) : Promise.resolve();
  return Promise.race([ready, new Promise((resolve) => setTimeout(resolve, REVEAL_TIMEOUT_MS))]);
}

const rightLook = () => [
  ["idle-right-in", TURN_MS],
  [Math.random() < 0.5 ? "idle-right-smile" : "idle-right", RIGHT_LOOK_MS],
  ["idle-right-out", TURN_MS],
];

// Returns the entries of the list in a random order. Each entry plays once before any entry plays again.
function rotator(list) {
  let order = [];
  let i = 0;
  const shuffle = () => {
    order = list.slice();
    for (let k = order.length - 1; k > 0; k--) {
      const j = Math.floor(Math.random() * (k + 1));
      [order[k], order[j]] = [order[j], order[k]];
    }
  };
  shuffle();
  return () => {
    if (i >= order.length) {
      i = 0;
      shuffle();
    }
    return order[i++];
  };
}

const hook = {
  mounted() {
    this.figure = this.el.querySelector(".pchat-stage__figure");
    this.dream = this.el.querySelector(".pchat-stage__dream");
    this.bubble = this.el.querySelector(".pchat-stage__dream-bubble");

    this.mood = null;
    this.shown = null;
    this.nextIdleVariation = rotator(IDLE_VARIATIONS);
    this.nextSleepVariation = rotator(SLEEP_VARIATIONS);
    this.onActivity = () => this.touch();
    for (const name of ACTIVITY_EVENTS) document.addEventListener(name, this.onActivity, { passive: true });
    this.el.addEventListener("click", () => {
      if (this.shown === "idle") this.playOnce("surprise");
    });
    this.applyMood();
    this.touch();
    this.scheduleLife();
    this.preloadRemaining();
  },

  updated() {
    this.applyMood();
  },

  destroyed() {
    clearTimeout(this.onceTimer);
    clearTimeout(this.sleepTimer);
    clearTimeout(this.lifeTimer);
    for (const name of ACTIVITY_EVENTS) document.removeEventListener(name, this.onActivity);
  },

  // Loads the other sprites when the browser is idle, so they do not slow down the first display of the panel.
  preloadRemaining() {
    const idleCallback = "requestIdleCallback" in window ? window.requestIdleCallback : window.setTimeout;
    idleCallback(() => {
      for (const name of Object.keys(STRIPS)) if (name !== this.shown) decode(name);
    });
  },

  // Opening another conversation can also change the mood from thinking to idle. Only the end of the work of the same
  // conversation plays the success animation.
  applyMood() {
    const mood = this.el.dataset.mood;
    const conversation = this.el.dataset.conversation;
    const sameConversation = conversation === this.conversation;
    this.conversation = conversation;
    if (mood === this.mood) return;
    const previous = this.mood;
    this.mood = mood;

    if (mood === "thinking") this.show("thinking");
    else if (mood === "error") this.playOnce("error");
    else if (mood === "attention") this.playOnce("surprise");
    // After a stop, the server sends `stopped`, not `idle`. So a stop does not play the success animation.
    else if (mood === "idle" && previous === "thinking" && sameConversation) this.playOnce("success");
    else this.show("idle");
  },

  // Decodes the sprite before it shows it. So a change of sprite never shows a frame that is only half drawn.
  show(name) {
    clearTimeout(this.onceTimer);
    this.shown = name;
    decode(name).then(() => {
      if (this.shown === name) this.animate(name);
    });
  },

  // A position of 100% puts the right edge of the strip on the right edge of the figure, so it shows the last frame.
  // `steps(n, jump-none)` shows each of the n frames, from the first frame to the last frame.
  animate(name) {
    const { frames, ms, iterations = Infinity, direction = "normal" } = STRIPS[name];
    this.figure.dataset.state = name;
    this.figure.style.backgroundImage = `url(${SPRITE_BASE}${name}.webp)`;
    this.animation?.cancel();
    if (REDUCED_MOTION.matches) return;
    this.animation = this.figure.animate([{ backgroundPositionX: "0" }, { backgroundPositionX: "100%" }], {
      duration: ms,
      iterations,
      direction,
      easing: `steps(${frames}, jump-none)`,
      fill: "forwards",
    });
  },

  playOnce(name) {
    this.show(name);
    this.onceTimer = setTimeout(() => this.show("idle"), ONCE_MS[name]);
  },

  touch() {
    this.lastActivity = performance.now();
    if (this.shown?.startsWith("sleep")) this.show("idle");
    if (!this.sleepTimer) this.scheduleSleep(SLEEP_AFTER_MS);
  },

  // A mouse move does not reset this timer, because that would cost too much. When the timer ends, it checks the time since
  // the last activity, and waits again if needed. A variation or another mood keeps the figure awake, so the timer waits for
  // the figure to be idle again. After the figure falls asleep, the next activity starts the timer again.
  scheduleSleep(ms) {
    this.sleepTimer = setTimeout(() => {
      const awakeLeft = SLEEP_AFTER_MS - (performance.now() - this.lastActivity);
      if (awakeLeft > 0) return this.scheduleSleep(awakeLeft);
      if (this.shown !== "idle") return this.scheduleSleep(SLEEP_RETRY_MS);
      this.sleepTimer = null;
      this.show("sleep");
    }, ms);
  },

  // A variation changes only the sprite. `mood` keeps the last value that the server set.
  scheduleLife() {
    clearTimeout(this.lifeTimer);
    this.lifeTimer = setTimeout(() => this.life(), 5000 + Math.random() * 5000);
  },

  life() {
    if (this.shown === "idle") {
      const variation = this.nextIdleVariation();
      this.play(variation === "idle-right" ? rightLook() : [[variation, VARIATION_MS[variation]]], "idle");
    } else if (this.shown === "sleep") {
      const variation = this.nextSleepVariation();
      if (variation === "dream") {
        this.showDream();
        this.scheduleLife();
      } else {
        this.play([[variation, VARIATION_MS[variation]]], "sleep");
      }
    } else {
      this.scheduleLife();
    }
  },

  // Shows each [sprite, ms] step for its duration, then goes back to the `base` sprite. If a new mood changes the sprite
  // during a step, the sequence stops.
  play([[name, ms], ...next], base) {
    this.show(name);
    this.lifeTimer = setTimeout(() => {
      const interrupted = this.shown !== name;
      if (!interrupted && next.length > 0) return this.play(next, base);
      if (!interrupted) this.show(base);
      this.scheduleLife();
    }, ms);
  },

  showDream() {
    this.bubble.textContent = "🍪".repeat(1 + Math.floor(Math.random() * 3));
    this.dream.classList.remove("is-shown");
    void this.dream.offsetWidth; // restarts the pop animation
    this.dream.classList.add("is-shown");
  },
};

export default hook;
